import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoginType } from '@app/core/enums';
import {
  CommonResponse,
  LoginOTP,
  SignInResult,
  Token,
} from '@app/core/interfaces';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { UserCountryService } from '@app/public/shared/services/user-country.service';
import { User } from '@app/pages/shared/interfaces/user.model';
import { ApiResponse } from '@app/core/models';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppConstant } from '../constant/app-constant';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly USER_ITEM = '_user';
  private readonly TOKEN_ITEM = '_token';

  baseUrl: string = environment.apiUrl;

  // userSubject can hold either a User or null
  public userSubject = new BehaviorSubject<User | null>(this._getUser());

  // tokenSubject can hold either a Token or null
  public tokenSubject = new BehaviorSubject<Token | null>(this._getToken());

  isLoggedIn = new BehaviorSubject<boolean>(this.loggedIn);
  rewardDialogShown: boolean = false; // For logged-in user reward dialogs
  nonLoggedInRewardDialogShown: boolean = false; // For non-logged-in claim dialog
  pendingRewards: { rewards: any[]; message?: string } | null = null;
  languageDialogShown: boolean = false;

  constructor(
    public http: HttpClient,
    public localStorageService: LocalStorageService,
    private readonly userCountryService: UserCountryService,
  ) {}

  // Getter for user
  get userValue(): User | null {
    return this.userSubject?.value;
  }

  // Getter for token
  get tokenValue(): Token | null {
    return this.tokenSubject?.value;
  }

  // Check if user is logged in based on user and token
  private get loggedIn(): boolean {
    const user = this.userSubject?.value;
    const token = this.tokenSubject?.value;
    return !!(user && token);
  }

  // Sync token with localStorage and across tabs
  private syncTokenAcrossTabs(token: Token | null): void {
    if (token) {
      localStorage.setItem(this.TOKEN_ITEM, JSON.stringify(token));
    } else {
      localStorage.removeItem(this.TOKEN_ITEM);
    }

    // Broadcast the token change across tabs using storage event
    window.dispatchEvent(new Event('storage'));
  }

  // Save token and sync across tabs
  _saveToken(token: Token): void {
    this.tokenSubject.next(token);
    this.syncTokenAcrossTabs(token); // Sync token to localStorage
  }

  // Retrieve token from localStorage
  _getToken(): Token | null {
    const token = localStorage.getItem(this.TOKEN_ITEM);
    return token ? JSON.parse(token) : null;
  }

  // Save user to localStorage and BehaviorSubject
  _saveUser(user: User): void {
    this.localStorageService.set(this.USER_ITEM, JSON.stringify(user));
    this.userSubject.next(user);
    this.isLoggedIn.next(true);
    // Reset reward dialog flag when user logs in so login rewards can show
    this.rewardDialogShown = false;
  }

  // Retrieve user from localStorage
  _getUser(): User | null {
    const user = this.localStorageService.get(this.USER_ITEM);
    return user ? JSON.parse(user) : null;
  }

  // Log out user and clear data
  logout(): void {
    this.localStorageService.remove(this.USER_ITEM);
    this.localStorageService.remove(this.TOKEN_ITEM);
    this.userSubject.next(null);
    this.tokenSubject.next(null);
    this.isLoggedIn.next(false);
  }

  // Method to login with OTP
  loginWithOTP(
    payload: LoginOTP,
    loginType: LoginType,
    {
      isVerify,
      isPassword,
      otpRequired,
      isWaNumber,
      defaultLanguage,
    }: {
      isVerify: boolean;
      isPassword?: boolean;
      otpRequired?: boolean;
      isWaNumber?: boolean;
      defaultLanguage?: string;
    },
  ): Observable<ApiResponse<any>> {
    let url = `${this.baseUrl}/user/login-with-otp?verify=${isVerify}&loginType=${loginType}&isPassword=${isPassword}&otpRequired=${otpRequired}&defaultLanguage=${defaultLanguage}`;

    if (isWaNumber !== undefined) {
      url = `${url}&isWaNumber=${isWaNumber}`;
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('debug') === 'true') {
      url = `${url}&debug=true`;
    }

    return this.http.post<ApiResponse<any>>(url, payload); // API call for OTP login
  }

  // Get list of genres (if needed for user preferences)
  getGenre(): Observable<CommonResponse> {
    return this.http.get<CommonResponse>(`${this.baseUrl}/genre`);
  }

  // Update user genre preferences
  updateGenre(prefIds: any): Observable<CommonResponse> {
    return this.http.put<CommonResponse>(
      `${this.baseUrl}/user/update-preference`,
      { preference: prefIds },
    );
  }

  // Sign up a film maker
  filmMakerSignup(signupInfo: any): Observable<SignInResult> {
    return this.http.post<SignInResult>(
      `${this.baseUrl}/user/signup`,
      signupInfo,
    );
  }

  // Switch user role (film maker or viewer)
  switchUserRole(
    role: string,
    isNotification: boolean,
  ): Observable<SignInResult> {
    return this.http.get<SignInResult>(
      `${this.baseUrl}/user/switch-role?role=${role}&isNotificationSend=${isNotification}`,
    );
  }

  // Verify email using token
  emailVerify(token: string): Observable<ApiResponse<{}>> {
    const headers = new HttpHeaders({
      deviceType: AppConstant.DEVICE_TYPE,
    });

    return this.http.get<ApiResponse<{}>>(
      `${this.baseUrl}/user/verify-email?token=${token}`,
      { headers },
    );
  }

  // Apply user role (switch role from viewer to film maker or vice versa)
  applyUserRole(rolename: string): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/user/switch-role?role=${rolename}`,
      {},
    );
  }

  // Get list of countries (for user country settings)
  getCountry(): Observable<CommonResponse> {
    return this.http.get<CommonResponse>(`${this.baseUrl}/user/country-list`);
  }

  // Set Default Language for user.
  setDefaultLanguage(data: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/user/default-language`, data);
  }

  // User Refresh Token
  getNewUserToken(): Observable<any> {
    return this.http.get(`${this.baseUrl}/user/refresh-token`);
  }
}
